/**
 * PricingAnalyticsSection
 *
 * SCRUM-1288: Guide Dashboard — Pricing Analytics Display
 * Shows pricing analytics for an experience derived from pricing_calculations.
 * Handles empty/zero state gracefully when no calculations exist yet.
 */

import React, { useState, useEffect } from 'react';
import { BarChart3, Loader2, AlertCircle } from 'lucide-react';
import { getPricingAnalytics } from '../../../services/guidePricingApi';
import { formatCentsToDollars } from '../../../utils/formHelpers';

export default function PricingAnalyticsSection({ experienceId }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchAnalytics() {
      try {
        setLoading(true);
        setError(null);
        const res = await getPricingAnalytics(experienceId);
        setAnalytics(res.data);
      } catch (err) {
        setError(err.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    }
    fetchAnalytics();
  }, [experienceId]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6" data-testid="pricing-analytics-section">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="h-5 w-5 text-emerald-600" />
          <h3 className="text-lg font-semibold">Pricing Analytics</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6" data-testid="pricing-analytics-section">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="h-5 w-5 text-emerald-600" />
          <h3 className="text-lg font-semibold">Pricing Analytics</h3>
        </div>
        <div className="p-3 bg-red-50 text-red-700 rounded-md flex items-center gap-2 text-sm" data-testid="analytics-error">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      </div>
    );
  }

  const isEmpty = !analytics || analytics.totalCalculations === 0;

  return (
    <div className="bg-white rounded-lg shadow p-6" data-testid="pricing-analytics-section">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="h-5 w-5 text-emerald-600" />
        <h3 className="text-lg font-semibold">Pricing Analytics</h3>
      </div>

      {isEmpty ? (
        <div className="text-center py-8 text-gray-500" data-testid="analytics-empty-state">
          <BarChart3 className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm font-medium">No booking data yet</p>
          <p className="text-xs text-gray-400 mt-1">
            Analytics will appear after your first completed booking.
          </p>
        </div>
      ) : (
        <div data-testid="analytics-data">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="analytics-stats">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900" data-testid="stat-calculations">
                {analytics.totalCalculations}
              </p>
              <p className="text-xs text-gray-500">Total Calculations</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900" data-testid="stat-bookings">
                {analytics.finalizedCount}
              </p>
              <p className="text-xs text-gray-500">Finalized Bookings</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-emerald-600" data-testid="stat-avg-value">
                {formatCentsToDollars(analytics.averageBookingValueCents)}
              </p>
              <p className="text-xs text-gray-500">Avg Booking Value</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900" data-testid="stat-conversion">
                {analytics.conversionRate}%
              </p>
              <p className="text-xs text-gray-500">Conversion Rate</p>
            </div>
          </div>

          {/* Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="analytics-breakdown">
            <div className="p-3 border rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Promotions</p>
              <p className="text-sm font-medium" data-testid="promo-usage">
                {analytics.promotions.usageCount} uses ({analytics.promotions.usageRate}%)
              </p>
              <p className="text-xs text-gray-400">
                Total discounts: {formatCentsToDollars(analytics.promotions.totalDiscountCents)}
              </p>
            </div>
            <div className="p-3 border rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Modifier Adjustments</p>
              <p className="text-sm font-medium" data-testid="modifier-total">
                {formatCentsToDollars(analytics.modifiers.totalAdjustmentCents)}
              </p>
            </div>
            <div className="p-3 border rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Add-on Revenue</p>
              <p className="text-sm font-medium" data-testid="addon-revenue">
                {formatCentsToDollars(analytics.addons.totalRevenueCents)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
